import partition from 'lodash/partition';

import {
  CohortGroupEntry,
  FiltersOperator,
  QueryBehaviorEntry,
  QueryMetricEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {createDefaultCohortMetricEntry} from 'common/report/util/cohorts/transformations/entries';
import {getInlineCustomPropertyId} from 'common/widgets/custom-property-modal/util';
import {ListOperator, NumberOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {PRIMATIVE_MATH_TYPES, PropertyType, ResourceType} from 'common/report/constants';
import {CustomProperty, CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SUPPORTED_BEHAVIORAL_AGGREGATIONS} from 'common/report/util/cohorts/utils';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS, PresetDateRange} from 'common/widgets/date-range-picker/models/presets';
import {Cohort} from 'common/widgets/cohorts-query-builder/models/cohort';
import {TOP_PEOPLE} from 'common/widgets/query-block/properties-menu/constants';
import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {InlineCohortSegment} from 'common/widgets/query-block/segment-menu/types';

type FiltersByResourceType = {
  [type in ResourceType]: PropertyFilter[];
};

function hasUniformResourceTypes(filters: PropertyFilter[]) {
  return filters?.length <= 1 || new Set(filters.map((filter) => filter.resourceType)).size === 1;
}

function groupProperyFiltersByResourceType(filters: PropertyFilter[]): FiltersByResourceType {
  return filters.reduce(
    (group, propertyFilter) => ({
      ...group,
      [propertyFilter.resourceType]: [...(group[propertyFilter.resourceType] || []), propertyFilter],
    }),
    {
      [ResourceType.Event]: [],
      [ResourceType.User]: [],
      [ResourceType.Cohort]: [],
      [ResourceType.Other]: [],
    } as FiltersByResourceType,
  );
}

function getBehavioralFilterFromQueryBehaviorEntry(
  entry: QueryBehaviorEntry,
  label = ``,
  frequency = new PropertyFilter({
    propertyType: PropertyType.Number,
    filterOperator: NumberOperator.AtLeast,
    filterValue: 1,
    resourceType: ResourceType.User,
  }),
): PropertyFilter {
  const customProperty: CustomProperty = {
    name: label,
    behavior: entry,
    description: ``,
    referencedBy: [],
    referencedDirectlyBy: [],
    referencedRawEventProperties: [],
    propertyType: frequency.propertyType,
    resourceType: CustomPropertyResourceType.People,
  };

  const customPropertyId = getInlineCustomPropertyId(null);

  return new PropertyFilter({
    ...frequency.getAttributes(),
    customProperty,
    customPropertyId,
    resourceType: ResourceType.User,
    ...(entry.property?.value
      ? {
          propertyName: entry.property?.value,
        }
      : {}),
  });
}

function getCohortEntriesFromBehavior(
  behavior: QueryBehaviorEntry,
  {
    label = ``,
    filters = [],
    filtersOperator = FiltersOperator.And,
    entryFiltersOperator = FiltersOperator.And,
    behavioralFiltersOperator = FiltersOperator.And,
    groupingOperator = FiltersOperator.And,
    frequency,
    frequencyExclusive,
  }: {
    label?: string;
    filters?: PropertyFilter[];
    filtersOperator?: FiltersOperator;
    entryFiltersOperator?: FiltersOperator;
    behavioralFiltersOperator?: FiltersOperator;
    groupingOperator?: FiltersOperator.And | FiltersOperator.Or;
    frequency?: PropertyFilter;
    frequencyExclusive?: boolean;
  } = {},
): CohortGroupEntry[] {
  let {event: eventFilters, user: userFilters, cohort: cohortFilters} = groupProperyFiltersByResourceType(filters);
  eventFilters = [...behavior.filters, ...eventFilters];

  let behaviorFilters = [
    getBehavioralFilterFromQueryBehaviorEntry(
      {
        ...behavior,
        filtersOperator,
        filters: eventFilters,
      },
      label,
      frequency,
    ),
  ];

  // Apply rational frequencies as two behavioral filters if frequency is exclusive (e.g. distribution on Insights)
  if (
    frequencyExclusive &&
    Array.isArray(frequency.filterValue) &&
    frequency.filterValue?.length > 1 &&
    !Number.isInteger(frequency.filterValue[1])
  ) {
    behaviorFilters = [
      getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          filtersOperator,
          filters: eventFilters,
        },
        label,
        new PropertyFilter({
          ...frequency.getAttributes(),
          filterOperator: NumberOperator.AtLeast,
          filterValue: frequency.filterValue[0],
        }),
      ),
      getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          filtersOperator,
          filters: eventFilters,
        },
        label,
        new PropertyFilter({
          ...frequency.getAttributes(),
          filterOperator: NumberOperator.LessThan,
          filterValue: frequency.filterValue[1],
        }),
      ),
    ];
  }

  const entry = {
    ...createDefaultCohortMetricEntry(),
    filters: [...userFilters, ...behaviorFilters],
    filtersOperator: entryFiltersOperator,
    behavioralFiltersOperator,
    groupingOperator,
  };

  return [
    entry,
    ...cohortFilters.map((filter) => ({
      ...createDefaultCohortMetricEntry(),
      filters: [filter],
      filtersOperator: FiltersOperator.And,
    })),
  ];
}

export function createNestedCohort(cohorts: Cohort[]) {
  const cohort = new Cohort();
  cohort.dataGroupId = cohorts[0].dataGroupId;

  /** if defining a single cohort, we expose the full definition
   * unlike multiple cohorts where we OR these are cohort filters */
  if (cohorts.length === 1) {
    return cohorts[0];
  }

  const createCohortFilter = (cohort: Cohort) =>
    new PropertyFilter({
      resourceType: ResourceType.Cohort,
      propertyType: PropertyType.List,
      propertyDefaultType: PropertyType.List,
      filterOperator: ListOperator.Contains,
      filterValue: [
        {
          cohort: {
            raw_cohort: cohort.serialize(),
            negated: false,
            name: cohort.name,
          },
        } as InlineCohortSegment,
      ],
    });

  cohort.groups = cohorts.map((cohort) => ({
    ...createDefaultCohortMetricEntry(),
    filters: [createCohortFilter(cohort)],
    groupingOperator: FiltersOperator.Or,
  }));

  return cohort;
}

export function translateMetricToCohort(
  {
    event,
    aggregationOperatorPerUser,
    property,
    filters,
    filtersOperator = FiltersOperator.And,
    dataGroupId,
  }: QueryMetricEntry,
  {
    name,
    dateRange = new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Last30Days]),
    frequency,
    frequencyExclusive = false,
    keepUserAggregation = false,
    filters: segmentFilters = [],
  }: {
    name?: string; // name of the clause
    dateRange?: DateRange;
    filters?: PropertyFilter[]; // Segment filters are anded together
    frequencyExclusive?: boolean;
    // Indicates whether user aggregation should be simplified to is set
    keepUserAggregation?: boolean;
    frequency?: PropertyFilter;
  } = {},
): Cohort {
  const {label} = event;
  let additionalFilters = segmentFilters;
  const [behavioralFilters, inlineFilters] = partition(filters, isFirstTimeEventFilter);
  const hasFirstTimeFilter = behavioralFilters.length > 0;
  // prevents a case where the entry has `or` filters with only one entry
  filtersOperator =
    filtersOperator === FiltersOperator.Or && inlineFilters.length <= 1 ? FiltersOperator.And : filtersOperator;

  const cohort = new Cohort();
  cohort.dataGroupId = dataGroupId;
  cohort.name = name;

  let behavior: QueryBehaviorEntry = {
    type: SelectorTypeDefinitions.Behavior,
    dateRange,
    event,
    filters: hasFirstTimeFilter ? behavioralFilters : [],
    filtersOperator,
    aggregationOperator: PRIMATIVE_MATH_TYPES.TOTAL,
    property: null,
    aggregationOperatorPerUser: null,
  };

  /** handles total per user entries  */
  if (property && SUPPORTED_BEHAVIORAL_AGGREGATIONS.includes(aggregationOperatorPerUser)) {
    /** for entries with anything other that distribution, we translate to "did event at least once" */
    if (!keepUserAggregation) {
      additionalFilters = [
        ...additionalFilters,
        new PropertyFilter({
          propertyType: PropertyType.String,
          propertyDefaultType: PropertyType.String,
          filterOperator: StringOperator.Set,
          propertyName: property.value,
          resourceType: property.resourceType,
        }),
      ];
    } else {
      behavior = {
        ...behavior,
        aggregationOperator: aggregationOperatorPerUser,
        property,
      };
    }
  }

  /** events with 1st time filters and global filters which apply after the fact of event occurence, we define those as first value of */
  if (hasFirstTimeFilter && additionalFilters.length) {
    additionalFilters = additionalFilters.map((filter) => {
      if (filter.resourceType !== ResourceType.Event) {
        return filter;
      }

      return getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          aggregationOperator: PRIMATIVE_MATH_TYPES.FIRST_VALUE,
          property: {
            label: null,
            resourceType: ResourceType.Event,
            propertyDefaultType: filter.propertyDefaultType ?? filter.propertyType,
            type: filter.propertyType,
            value: filter.propertyName,
          },
          filtersOperator,
          filters: inlineFilters.filter((filter) => filter.resourceType === ResourceType.Event),
        },
        label,
        filter,
      );
    });
  }

  /** profile mode without aggregations translates directly to a cohort definition with filters */
  if (event.value === TOP_PEOPLE.value) {
    cohort.groups = [
      {
        ...createDefaultCohortMetricEntry(),
        filters: [...inlineFilters, ...additionalFilters],
        filtersOperator,
      },
    ];
    // Apply event filters for common condition
    /** @todo: [pablo] Use the same hasUniformResourceTypes approach for query simplification */
  } else if (
    filtersOperator === FiltersOperator.And ||
    ((additionalFilters?.length === 0 || hasFirstTimeFilter) && hasUniformResourceTypes(inlineFilters))
  ) {
    cohort.groups = getCohortEntriesFromBehavior(behavior, {
      label,
      filters: [...inlineFilters, ...additionalFilters],
      filtersOperator,
      entryFiltersOperator: filtersOperator,
      frequency,
      frequencyExclusive,
    });
  } else if (filtersOperator === FiltersOperator.Or) {
    // Create n entries for or-ed filters with a and-ed segment filter
    cohort.groups = inlineFilters.reduce(
      (entries, filter) => [
        ...entries.map((entry, i) => {
          return i === entries.length - 1
            ? {
                ...entry,
                groupingOperator: FiltersOperator.Or,
              }
            : entry;
        }),
        ...getCohortEntriesFromBehavior(behavior, {
          label,
          filters: [filter, ...additionalFilters],
          filtersOperator: FiltersOperator.And,
          groupingOperator: FiltersOperator.And,
          frequency,
          frequencyExclusive,
        }),
      ],
      [],
    );
  } else {
    throw new Error(`Invalid filtersOperator ${filtersOperator}, only and/or are supported for translation`);
  }

  return cohort;
}
